import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { QRCodeDisplay } from '../components/ui/QRCodeDisplay';
import { useToast } from '../components/ui/toast';
import { getRegistrationByQR, RegistrationData } from '../lib/api';
import { formatPhoneNumber } from '../lib/utils';

/**
 * 등록 완료 페이지
 * 사전등록이 성공적으로 완료된 후 보여지는 페이지입니다.
 */
const RegistrationCompletePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { showToast } = useToast();
  
  const [registrationData, setRegistrationData] = useState<RegistrationData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // 지역 표시 이름 변환
  const getRegionDisplayName = (region: string) => {
    switch (region) {
      case 'seoul': return '서울';
      case 'busan': return '부산';
      case 'daegu': return '대구';
      case 'incheon': return '인천';
      case 'gwangju': return '광주';
      case 'daejeon': return '대전';
      case 'ulsan': return '울산';
      case 'sejong': return '세종';
      case 'gyeonggi': return '경기도';
      case 'gangwon': return '강원도';
      case 'chungbuk': return '충청북도';
      case 'chungnam': return '충청남도';
      case 'jeonbuk': return '전라북도';
      case 'jeonnam': return '전라남도';
      case 'gyeongnam': return '경상남도';
      case 'gyeongbuk': return '경상북도';
      case 'jeju': return '제주도';
      case 'other': return 'etc';
      default: return region;
    }
  };

  // 등록 데이터 로드
  useEffect(() => {
    const loadRegistrationData = async () => {
      try {
        setIsLoading(true);
        
        // 먼저 state에서 데이터 확인 (직접 등록 완료 후 온 경우)
        const stateData = location.state?.registrationData;
        if (stateData && stateData.id === id) {
          setRegistrationData(stateData);
          return;
        }
        
        // URL로 직접 접근한 경우 API에서 데이터 조회
        if (id) {
          const data = await getRegistrationByQR(id);
          if (data) {
            setRegistrationData(data);
          } else {
            showToast('등록 정보를 찾을 수 없습니다.', 'error');
            navigate('/pre-register');
          }
        } else {
          navigate('/pre-register');
        }
      } catch (error) {
        console.error('등록 데이터 로드 실패:', error);
        showToast('등록 정보를 불러오는데 실패했습니다.', 'error');
        navigate('/pre-register');
      } finally {
        setIsLoading(false);
      }
    };

    loadRegistrationData();
  }, [id, location.state, navigate, showToast]);
  
  // 로딩 중 표시
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">등록 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }
  
  // 등록 데이터가 없으면 사전등록 페이지로 리다이렉트
  if (!registrationData) {
    return null;
  }

  // 홈으로 이동
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleGoHome = () => {
    navigate('/pre-register');
  };

  // 관리자 페이지로 이동
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleGoAdmin = () => {
    navigate('/admin/login');
  };

  // QR 코드 데이터 준비 (등록번호만 사용)
  const qrCodeData = {
    id: registrationData.id || id!,
    name: registrationData.name,
    phone: registrationData.phone,
    registration_id: registrationData.registration_id || '',
    created_at: registrationData.created_at || new Date().toISOString(),
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 간단한 헤더 */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-bold text-gray-900">전시회 현장등록</h1>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          
          {/* 성공 메시지 */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              현장등록이 완료되었습니다!
            </h1>
            <p className="text-lg text-gray-600">
              아래 QR 코드를 저장하여 전시회 입장 시 사용해주세요.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* 등록자 정보 */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">등록자 정보</h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">이름</span>
                  <span className="font-medium">{registrationData.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">전화번호</span>
                  <span className="font-medium">{formatPhoneNumber(registrationData.phone)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">이메일</span>
                  <span className="font-medium">{registrationData.email || '미입력'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">국적</span>
                  <span className="font-medium">
                    {registrationData.nationality === 'foreigner' ? '외국인 (Foreigner)' : 
                     registrationData.nationality === 'domestic' ? '내국인' : '미설정'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">성별</span>
                  <span className="font-medium">{registrationData.gender === 'male' ? '남성' : '여성'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">연령대</span>
                  <span className="font-medium">
                    {registrationData.age_group === 'teen' && '10대'}
                    {registrationData.age_group === 'twenties' && '20대'}
                    {registrationData.age_group === 'thirties' && '30대'}
                    {registrationData.age_group === 'forties' && '40대'}
                    {registrationData.age_group === 'fifties' && '50대'}
                    {registrationData.age_group === 'sixties_plus' && '60대 이상'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">지역</span>
                  <span className="font-medium">{getRegionDisplayName(registrationData.region)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">전시회</span>
                  <span className="font-medium">
                    {registrationData.exhibition_type === 'urbanbreak' ? 'UrbanBreak' : 'Toy Con Seoul'}
                  </span>
                </div>
              </div>
            </div>

            {/* QR 코드 */}
            <div>
              <QRCodeDisplay 
                data={qrCodeData}
                size={256}
                showDownloadButton={true}
                showInfo={true}
              />
            </div>
          </div>

          {/* 안내 메시지 */}
          <Card className="mt-8">
            <CardHeader>
              <CardTitle className="text-xl">전시회 안내</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 text-gray-700">
                <div>
                  <h4 className="font-semibold text-lg mb-2">📅 전시회 일정</h4>
                  <p>• 날짜: 2025년 8월 7일 (목) ~ 10일 (일)</p>
                  <p>• 시간: 오전 10시 ~ 오후 6시</p>
                  <p>• 장소: 서울 코엑스 Hall B</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-lg mb-2">🎫 입장 안내</h4>
                  <p>• 위 QR 코드를 입장구에서 제시해주세요</p>
                  <p>• 스마트폰 또는 인쇄본 모두 가능합니다</p>
                  <p>• 신분증을 함께 지참해주세요</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-lg mb-2">📞 문의사항</h4>
                  <p>• 전화: 02-1661-0118</p>
                  <p>• 이메일: info@urbanbreak.com</p>
                  <p>• 운영시간: 평일 오전 9시 ~ 오후 6시</p>
                </div>
              </div>
            </CardContent>
          </Card>

        </div>
      </div>
    </div>
  );
};

export default RegistrationCompletePage; 